/**
 * `DocDiff` → the rows and the summary line the before/after view draws.
 *
 * The view itself (diff-view.ts) knows about windowing and word-level
 * highlighting and nothing about the diff's shape. This file is the seam, and
 * every rule in it is one that CLEAN's contract states and a naive reader gets
 * wrong:
 *
 *   1. **Counts come from `DiffCounts`, never from array lengths.**
 *      `countsOnly` and `window` both narrow the arrays and neither narrows the
 *      counts. A 40 000-cue diff is requested `countsOnly` per slider tick — the
 *      arrays are empty by design and `"0 changes"` would be a lie the user acts
 *      on. `truncated` says which mode produced the arrays.
 *
 *   2. **Restructured is its own kind.** Split/merge/resegment lineage is
 *      derived once by CLEAN, and cues in a restructured group are EXCLUDED
 *      from added/removed. Rendering them as "removed 1, added 2" describes the
 *      data structure instead of what happened, and one split cue is the single
 *      most common thing this product does.
 *
 *   3. **No cue changed ≠ no changes.** `ColorOp{scope:'style'}` recolours the
 *      whole file while touching zero cues. `styles`/`tracks`/`regions`/
 *      `metaChanged` are why those sections exist, and a summary that ignores
 *      them reports "no changes" for an operation the user can plainly see.
 *
 *   4. **`revOnlyCount` is unchanged.** A cue whose `rev` moved while every
 *      field compared equal is churn nobody can see. It is surfaced separately
 *      so it can be explained, not counted as a change.
 */

import {
  type Cue,
  type DocDiff,
  type Ticks,
  TICKS_PER_SECOND,
} from '../../../../../contracts/cue-model.ts';
import { plainText } from '../../../../../src/core/model/text.ts';
import { formatTimestamp, timeFormat } from '../../../../../src/core/time/timestamp.ts';
import { clock } from './format.ts';
import type { DiffRow } from './diff-view.ts';

/**
 * A tick is 1/120 000 s, NOT a millisecond — 120 000 is divisible by 24, 25,
 * 30 and 1001-based rates, which is why the contract chose it. Dividing by
 * 1000 here would put every timestamp out by 120×, and it would look plausible.
 */
function at(start: Ticks): string {
  return clock(Math.floor(Number(start) / TICKS_PER_SECOND));
}

function textOf(cue: Cue): string {
  return plainText(cue).replace(/\s+/g, ' ').trim();
}

/** The cue table's own dialect (cue-window.ts), so a span reads the same in both. */
const SPAN_FORMAT = timeFormat('vtt');

function spanOf(cue: Cue): string {
  return `${formatTimestamp(cue.time.start, SPAN_FORMAT)} → ${formatTimestamp(cue.time.end, SPAN_FORMAT)}`;
}

/**
 * One side of a modified cue: its text, led by whatever else moved. Without
 * the lead a shift, a sync or a framerate change draws two identical panes
 * under "~ changed", and so does a merge, whose only change to the first
 * file's cues is their number. First, because the pane clamps to two lines.
 */
function sideOf(cue: Cue, fields: readonly string[]): string {
  const lead: string[] = [];
  if (fields.includes('time')) lead.push(spanOf(cue));
  else if (fields.includes('index') && !fields.includes('text') && cue.index !== null) {
    lead.push(`Subtitle ${cue.index}`);
  }
  return lead.length > 0 ? `${lead.join(' · ')} · ${textOf(cue)}` : textOf(cue);
}

/** What the page did, where it changes how a diff reads. */
export interface DiffContext {
  /**
   * The diff is the input against part one of a split, so a cue missing from
   * it went to part two. Read as "removed", the page appeared to lose half
   * the file (live, 2026-10-06).
   */
  readonly split?: boolean;
}

/**
 * Flatten a diff into display rows, in document order.
 *
 * Ordering is by start time across all four buckets rather than
 * added-then-removed-then-modified: a before/after view read top to bottom is
 * the only way a user checks a clean-up, and grouping by change kind makes that
 * impossible.
 */
export function diffRows(diff: DocDiff, context: DiffContext = {}): readonly DiffRow[] {
  const rows: { sort: number; row: DiffRow }[] = [];
  const push = (start: Ticks, row: Omit<DiffRow, 'n' | 'time'>) => {
    const s = Number(start);
    rows.push({ sort: s, row: { n: 0, time: at(start), ...row } });
  };

  for (const cue of diff.added)
    push(cue.time.start, { change: 'added', before: '', after: textOf(cue) });
  for (const cue of diff.removed) {
    push(cue.time.start, {
      change: context.split ? 'moved' : 'removed',
      before: textOf(cue),
      after: '',
    });
  }
  for (const { before, after, fields } of diff.modified) {
    push(after.time.start, {
      change: 'edited',
      before: sideOf(before, fields),
      after: sideOf(after, fields),
    });
  }
  // RULE 2. One row for the whole group, both sides joined, so a 1→2 split reads
  // as one thing that happened rather than three unrelated cue events.
  for (const group of diff.restructured) {
    const first = group.after[0] ?? group.before[0];
    if (!first) continue;
    push(first.time.start, {
      change: 'restructured',
      before: group.before.map(textOf).join(' '),
      after: group.after.map(textOf).join(' ⏎ '),
    });
  }

  rows.sort((a, b) => a.sort - b.sort);
  return rows.map((r, i) => ({ ...r.row, n: i + 1 }));
}

export interface DiffSummary {
  /** The legend sentence. Always true, in every diff mode. */
  readonly text: string;
  /** true ⇒ the rows are a window or absent; the view must not imply completeness. */
  readonly truncated: boolean;
  /** true ⇒ nothing changed anywhere, cues and entities alike. */
  readonly empty: boolean;
}

/** In-session bookkeeping on DocumentMeta (what made the document, from which ids). No writer emits it. */
const LINEAGE_KEYS: ReadonlySet<string> = new Set(['producedBy', 'parents']);

const PLURAL = (n: number, one: string, many: string): string =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * The legend, built from the counts alone so it survives `countsOnly`.
 *
 * ⚠ NEW USER-VISIBLE COPY, drafted not approved — `docs/USER_JOURNEY.md`
 * describes the before/after view but writes no legend. Recorded in
 * web/COPY_DECISIONS.md and raised in comms/UI.md.
 */
export function diffSummary(diff: DocDiff, context: DiffContext = {}): DiffSummary {
  const parts: string[] = [];
  if (diff.modifiedCount > 0)
    parts.push(PLURAL(diff.modifiedCount, 'line changed', 'lines changed'));
  if (diff.addedCount > 0) parts.push(PLURAL(diff.addedCount, 'line added', 'lines added'));
  if (diff.removedCount > 0) {
    parts.push(
      context.split
        ? PLURAL(diff.removedCount, 'line goes to part 2', 'lines go to part 2')
        : PLURAL(diff.removedCount, 'line removed', 'lines removed'),
    );
  }
  if (diff.restructuredCount > 0) {
    parts.push(PLURAL(diff.restructuredCount, 'line split or merged', 'lines split or merged'));
  }

  // RULE 3. Entity-only changes are real changes and must not read as none.
  // But only ones a file can show: a merge of two SRTs adds the second file's
  // synthetic placeholder style and moves the lineage keys, and the legend
  // told an SRT user "styles and file details changed" (live, 2026-10-06).
  const entities: string[] = [];
  const authored = (styles: readonly { readonly isSynthetic?: boolean }[]) =>
    styles.filter((s) => !s.isSynthetic).length;
  if (
    authored(diff.styles.added) + authored(diff.styles.removed) + diff.styles.modified.length >
    0
  ) {
    entities.push('styles');
  }
  if (diff.tracks.added.length + diff.tracks.removed.length + diff.tracks.modified.length > 0) {
    entities.push('tracks');
  }
  if (diff.regions.added.length + diff.regions.removed.length + diff.regions.modified.length > 0) {
    entities.push('regions');
  }
  if (diff.metaChanged.some((key) => !LINEAGE_KEYS.has(key))) entities.push('file details');

  const cueChanges =
    diff.modifiedCount + diff.addedCount + diff.removedCount + diff.restructuredCount;
  const empty = cueChanges === 0 && entities.length === 0 && !diff.reordered;

  if (empty) {
    // `revOnlyCount` is the case worth explaining: something ran, nothing you
    // can see moved. Saying only "no changes" invites a bug report.
    const text =
      diff.revOnlyCount > 0
        ? 'Nothing changed in the subtitles themselves.'
        : 'No changes — the file is the same.';
    return { text, truncated: diff.truncated, empty: true };
  }

  if (parts.length === 0) {
    // Entities changed and no cue did — the ColorOp{scope:'style'} case.
    parts.push(`${entities.join(' and ')} changed`);
  } else if (entities.length > 0) {
    parts.push(`${entities.join(' and ')} changed`);
  }
  if (diff.reordered) parts.push('the order changed');

  const list =
    parts.length === 1
      ? (parts[0] as string)
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] as string}`;
  const unchanged =
    diff.unchangedCount > 0 ? `, ${diff.unchangedCount.toLocaleString()} unchanged` : '';
  return { text: `${list}${unchanged}.`, truncated: diff.truncated, empty: false };
}

/* ── QC table ─────────────────────────────────────────────────────────── */

export interface QcRow {
  readonly code: string;
  readonly label: string;
  readonly count: number;
  readonly severity: 'error' | 'warning' | 'info';
  readonly detail: string;
}

/**
 * Diagnostics grouped for the QC table.
 *
 * Sorted by severity then count, because a table sorted by code buries the one
 * `error` under twenty `info` rows and the whole reason to show a QC table is
 * that one row.
 */
export function qcRows(
  diagnostics: readonly {
    readonly code: string;
    readonly severity: 'error' | 'warning' | 'info';
    readonly detail?: string;
    readonly count?: number;
  }[],
): readonly QcRow[] {
  const byCode = new Map<string, QcRow>();
  for (const d of diagnostics) {
    const existing = byCode.get(d.code);
    const count = d.count ?? 1;
    if (existing === undefined) {
      byCode.set(d.code, {
        code: d.code,
        label: QC_COPY[d.code] ?? d.code,
        count,
        severity: d.severity,
        detail: d.detail ?? '',
      });
      continue;
    }
    byCode.set(d.code, {
      ...existing,
      count: existing.count + count,
      severity: rank(d.severity) > rank(existing.severity) ? d.severity : existing.severity,
    });
  }
  const order = [...byCode.values()];
  order.sort((a, b) => rank(b.severity) - rank(a.severity) || b.count - a.count);
  return order;
}

function rank(s: 'error' | 'warning' | 'info'): number {
  return s === 'error' ? 2 : s === 'warning' ? 1 : 0;
}

/**
 * ⚠ THE LAST SENTENCE TABLE IN TYPESCRIPT, and it is here on purpose rather
 * than by omission. The loss sentences moved to `macros/states.html` this round
 * because two surfaces said them; these are said by ONE surface.
 *
 * That surface now exists: /subtitle-reading-speed-checker mounts the report
 * (pages/tool.ts, `paintQc`). It still reads these from here because the
 * markup it would move into (tools/tool.html, macros/states.html) was being
 * edited on other branches when it landed. Moving them is the next step: one
 * home in Jinja, cloned here. Named in comms/UI.md so it is a decision rather
 * than a leftover.
 *
 * Approved in S17. An unlisted code falls back to the code itself rather than
 * to silence — the diagnostic vocabulary belongs to the feature engines.
 */
const QC_COPY: Readonly<Record<string, string>> = {
  'fps-missing': 'No framerate in the file — frame-based timings could not be resolved',
  'fps-inherited': 'Framerate taken from another track',
  'resolution-assumed': 'Screen size assumed — positions may be off',
  'encoding-low-confidence': 'Character encoding was guessed',
  overlap: 'Lines overlap in time',
  'negative-duration': 'A line ends before it starts',
  'zero-duration': 'A line has no duration',
  'too-short': 'Lines shorter than the minimum time on screen',
  'reading-speed-high': 'Lines go by faster than most people read',
  'line-too-long': 'Lines longer than the usual limit',
  'too-many-lines': 'More lines on screen than the usual limit',
  'segmenter-fallback':
    'Line widths were estimated — this browser could not split text into characters the way the check needs',
  // What the reading-speed checker says when its report has no findings. Not
  // "the file is good": `qcView` only knows that nothing crossed a threshold.
  'nothing-flagged': 'Nothing flagged at these settings',
};

/** One QC sentence by code; an unlisted code shows itself, as in `qcRows`. */
export function qcSentence(code: string): string {
  return QC_COPY[code] ?? code;
}

/** Diagnostic codes with no sentence yet — the ledger, same as loss codes. */
export function uncopiedDiagnostics(codes: readonly string[]): readonly string[] {
  return codes.filter((c) => QC_COPY[c] === undefined);
}

/**
 * A `QcReport` split into the two things it actually contains.
 *
 * The contract is explicit that these are different, and the split is the whole
 * value of this function: `diagnostics` are findings about the RUN, and
 * `segmenter-fallback` in particular means *every* line-width and cps number in
 * the same report was approximated. Rendering it as one more row would put
 * "widths may be wrong" UNDERNEATH the width count it invalidates, sorted by
 * severity into the middle of a list the reader is already treating as
 * measurement.
 *
 * So caveats come out separately and belong above the table.
 */
export interface QcView {
  /** Findings about the cues — the table. */
  readonly findings: readonly QcRow[];
  /** Findings about the RUN — printed above the table, qualifying every number in it. */
  readonly caveats: readonly QcRow[];
  /** true ⇒ nothing was flagged. Not the same as "the file is good". */
  readonly clean: boolean;
}

interface QcReportish {
  readonly overCps?: readonly unknown[];
  readonly overLineWidth?: readonly unknown[];
  readonly tooShort?: readonly unknown[];
  readonly overlaps?: readonly unknown[];
  readonly diagnostics?: readonly {
    readonly code: string;
    readonly severity: 'error' | 'warning' | 'info';
    readonly message?: string;
  }[];
}

export function qcView(report: QcReportish): QcView {
  // Severities are the UI's call, not the report's: these arrays carry CueIds
  // and no severity at all. Reading speed is the one a viewer actually notices,
  // so it is the warning; the rest are informational counts.
  const findings = qcRows(
    [
      { code: 'reading-speed-high', severity: 'warning' as const, n: report.overCps?.length ?? 0 },
      { code: 'line-too-long', severity: 'info' as const, n: report.overLineWidth?.length ?? 0 },
      // `tooShort` is every cue under the profile's MINIMUM time on screen
      // (5/6 s for Netflix English, `minDurationTicks` in qc.ts), not only a
      // cue of zero length. It was labelled 'zero-duration', and a 0.5 s cue
      // on the reading-speed page read as "A line has no duration".
      { code: 'too-short', severity: 'warning' as const, n: report.tooShort?.length ?? 0 },
      { code: 'overlap', severity: 'info' as const, n: report.overlaps?.length ?? 0 },
    ]
      // A finding with a count of zero is not a finding. A table of four rows
      // reading 0, 0, 0, 0 makes a clean file look inspected-and-suspicious.
      .filter((f) => f.n > 0)
      .map((f) => ({ code: f.code, severity: f.severity, count: f.n })),
  );

  const caveats = qcRows(
    (report.diagnostics ?? []).map((d) => ({
      code: d.code,
      severity: d.severity,
      detail: d.message ?? '',
    })),
  );

  return { findings, caveats, clean: findings.length === 0 };
}
